import type {
  ExpensePart,
  ExpenseSubcategory,
  ExpenseTax,
  UpdateExpensePart,
} from '@pflegenavi/frontend/api-nursing-home';
import { v4 as uuid } from 'uuid';

/**
 * Editor-side part. `_internalId` is a client-only stable identity used as
 * the React row key and for update/remove targeting. Server-loaded parts
 * mirror it from their server `id`; newly-added (unsaved) parts get a
 * `new-${uuid()}` value. Matches the receipt-batch `LineItem._internalId`
 * convention so the editor pattern stays consistent across modules.
 */
export interface PartDraft extends UpdateExpensePart {
  _internalId: string;
}

/** Client-only id assigned to newly-added (unsaved) parts. */
export function createInternalId(): string {
  return `new-${uuid()}`;
}

/**
 * Map server-side parts to editor drafts. `_internalId` mirrors the server
 * `id` so saved parts keep a stable key across reloads.
 */
export function expensePartsToDrafts(parts: ExpensePart[]): PartDraft[] {
  return parts.map((p) => ({
    _internalId: p.id,
    id: p.id,
    amount: p.amount,
    expense_subcategory_id: p.expense_subcategory?.id ?? null,
    expense_tax_id: p.expense_tax_id,
    commentary: p.commentary,
  }));
}

/** A 0€ part is never a valid line item — block submitting with one. */
export function hasZeroAmountPart(parts: PartDraft[]): boolean {
  return parts.some((p) => (p.amount || 0) === 0);
}

/**
 * True if any part is assigned a subcategory that has been archived. Archived
 * subcategories must be re-picked before the expense can be saved/exported.
 */
export function hasArchivedSubcategoryPart(
  parts: PartDraft[],
  subcategories: ExpenseSubcategory[]
): boolean {
  return parts.some((p) => {
    if (!p.expense_subcategory_id) {
      return false;
    }
    const sub = subcategories.find((s) => s.id === p.expense_subcategory_id);
    return sub?.archived === true;
  });
}

/**
 * True if any part is assigned a tax that has been archived. Archived taxes
 * must be re-picked before the expense can be saved/exported.
 */
export function hasArchivedTaxPart(
  parts: PartDraft[],
  taxes: ExpenseTax[]
): boolean {
  return parts.some(
    (p) =>
      p.expense_tax_id != null &&
      taxes.find((tax) => tax.id === p.expense_tax_id)?.archived === true
  );
}

/**
 * Convert drafts to the API payload. `_internalId` is dropped (client-only).
 * For new parts, `id` is undefined so the backend treats them as creates.
 */
export function draftsToUpdateParts(parts: PartDraft[]): UpdateExpensePart[] {
  return parts.map((p) => {
    const out: UpdateExpensePart = {
      amount: p.amount,
      expense_subcategory_id: p.expense_subcategory_id ?? null,
      expense_tax_id: p.expense_tax_id ?? null,
      commentary: p.commentary?.trim() ? p.commentary.trim() : null,
    };
    if (p.id) {
      out.id = p.id;
    }
    return out;
  });
}
